import { Controller } from "stimulus"
import { sendEmailQueue } from "../lib/email_send_queue"

export default class extends Controller {
  static targets = ["submit", "progress", "currentRecipient", "summary", "cancel", "failures"]

  connect() {
    this.sending = false
    this.cancelRequested = false
    this.beforeUnload = (event) => {
      if (!this.sending) return
      event.preventDefault()
      event.returnValue = ""
    }
    this.beforeVisit = (event) => {
      if (!this.sending) return
      event.preventDefault()
      window.alert("Email sending is in progress. Stop sending before leaving this page.")
    }
    document.addEventListener("turbolinks:before-visit", this.beforeVisit)
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.beforeUnload)
    document.removeEventListener("turbolinks:before-visit", this.beforeVisit)
  }

  async submit(event) {
    if (this.sending) return

    const form = event.currentTarget
    const recipientIds = Array.from(form.querySelectorAll("input[name='user_ids[]']")).map((input) => input.value)
    if (recipientIds.length === 0) return

    event.preventDefault()
    await this.run(form, recipientIds)
  }

  cancel() {
    this.cancelRequested = true
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Stopping after the current email…"
  }

  async run(form, recipientIds) {
    const formEntries = Array.from(new FormData(form).entries())
    this.sending = true
    document.body.classList.add("email-queue-active")
    this.cancelRequested = false
    this.counts = { sent: 0, skipped: 0, failed: 0, unknown: 0 }
    this.failures = []
    this.setControlsDisabled(true)
    this.showProgress()
    window.addEventListener("beforeunload", this.beforeUnload)

    await sendEmailQueue({
      form,
      recipientIds,
      formEntries,
      cancelled: () => this.cancelRequested,
      onStart: (current) => this.renderCurrentRecipient(current),
      onResult: (result) => this.recordResult(result, recipientIds.length)
    })

    this.sending = false
    document.body.classList.remove("email-queue-active")
    window.removeEventListener("beforeunload", this.beforeUnload)
    this.setControlsDisabled(false)
    this.finish(recipientIds.length)
  }

  recordResult(result, total) {
    const status = Object.prototype.hasOwnProperty.call(this.counts, result.status) ? result.status : "unknown"
    this.counts[status] += 1
    if (status === "failed" || status === "unknown") {
      this.failures.push(result)
      this.renderFailure(result, status)
    }
    this.renderProgress(total)
  }

  renderProgress(total) {
    const processed = Object.values(this.counts).reduce((sum, count) => sum + count, 0)
    if (this.hasProgressTarget) this.progressTarget.textContent = `Emails are being sent — ${processed} of ${total} processed`
    if (this.hasSummaryTarget) {
      this.summaryTarget.textContent = `${this.counts.sent} sent · ${this.counts.skipped} skipped · ${this.counts.failed} failed · ${this.counts.unknown} uncertain`
    }
  }

  finish(total) {
    const processed = Object.values(this.counts).reduce((sum, count) => sum + count, 0)
    this.renderProgress(total)
    if (this.hasProgressTarget) {
      this.progressTarget.textContent = this.cancelRequested ? `Stopped after ${processed} of ${total}` : `Finished processing ${processed} emails`
    }
    if (this.hasCurrentRecipientTarget) {
      this.currentRecipientTarget.textContent = this.cancelRequested ? "Sending stopped." : "All selected emails have been processed."
    }
    if (this.hasSummaryTarget && this.failures.length > 0) {
      this.summaryTarget.textContent += ". Failed or uncertain recipients remain available after reloading."
    }
    if (!this.cancelRequested && this.failures.length === 0) {
      window.setTimeout(() => {
        if (this.element.dataset.emailSendQueueCompletionUrl) {
          window.location.assign(this.element.dataset.emailSendQueueCompletionUrl)
        } else {
          window.location.reload()
        }
      }, 1000)
    }
  }

  renderFailure(result, status) {
    if (!this.hasFailuresTarget) return
    const item = document.createElement("li")
    const input = Array.from(this.element.querySelectorAll("input[name='user_ids[]']")).find((element) => element.value === result.recipientId)
    const email = (result.payload && result.payload.recipient_email) || (input && input.dataset.recipientEmail)
    item.textContent = `${email || `Recipient ${result.recipientId}`}: ${status === "failed" ? "not sent" : "delivery uncertain"}`
    this.failuresTarget.append(item)
  }

  renderCurrentRecipient({ recipientId, index, total }) {
    if (!this.hasCurrentRecipientTarget) return
    const input = Array.from(this.element.querySelectorAll("input[name='user_ids[]']")).find((element) => element.value === recipientId)
    const email = input && input.dataset.recipientEmail
    this.currentRecipientTarget.textContent = `Sending ${index} of ${total}${email ? ` to ${email}` : ""}…`
  }

  showProgress() {
    if (this.hasProgressTarget) this.progressTarget.closest("[data-email-queue-status]").hidden = false
    this.renderProgress(this.element.querySelectorAll("input[name='user_ids[]']").length)
  }

  setControlsDisabled(disabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = disabled
    if (this.hasCancelTarget) this.cancelTarget.hidden = !disabled
  }
}
